import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material'
import { MouseEvent, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useMergeCustomersMutation,
  useUpdateCustomerDetailMutation
} from '../../redux/api'
import { CustomerEntry } from '../../sdk'
import { CustomerLookupInput } from '../CustomerLookUp'

interface Props {
  customer: CustomerEntry
}

export const CustomerDeleteMenu = ({ customer }: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const menuOpen = Boolean(menuAnchor)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false)
  const [newCustomer, setNewCustomer] = useState<CustomerEntry>()

  const [updateCustomer] = useUpdateCustomerDetailMutation()
  const [mergeCustomers] = useMergeCustomersMutation()

  const handleDelete = (toArchive: boolean) => {
    updateCustomer({
      authorization: accessToken,
      customerEntry: {
        ...customer,
        information: {
          ...customer.information,
          archived: toArchive ? new Date() : undefined
        }
      }
    })
  }
  const handleMerge = () => {
    if (!newCustomer) {
      return
    }
    // TODO: pick back up here
    mergeCustomers({
      authorization: accessToken,
      mergeCustomersRequest: {
        shop: shopId,
        customerToKeep: newCustomer.id,
        customerToArchive: customer.id
      }
    })
  }

  const closeDeleteDialog = () => setDeleteDialogOpen(false)
  const closeMergeDialog = () => setMergeDialogOpen(false)

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
    setMenuAnchor(null)
  }
  const handleMergeClick = () => {
    setMergeDialogOpen(true)
    setMenuAnchor(null)
  }

  return (
    <>
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>
          Confirm {customer.information!.archived ? 'Unarchive' : 'Archive'}{' '}
          Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{' '}
            {customer.information!.archived ? 'unarchive' : 'archive'} this
            customer?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            onClick={() => {
              customer.information!.archived
                ? handleDelete(false)
                : handleDelete(true)
              closeDeleteDialog()
            }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mergeDialogOpen} onClose={closeMergeDialog}>
        <DialogTitle>Confirm merge Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to merge this customer with another customer?
            This action will archive the current customer.
          </DialogContentText>
          <Box paddingTop={2}>
            <CustomerLookupInput
              customer={newCustomer}
              handleCustomerChange={(customer) => setNewCustomer(customer)}
              customerToHide={customer.id || undefined}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMergeDialog}>Cancel</Button>
          <Button
            disabled={!newCustomer}
            onClick={() => {
              handleMerge()
              closeMergeDialog()
            }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <IconButton
        onClick={(event: MouseEvent<HTMLElement>) => {
          setMenuAnchor(event.currentTarget)
        }}>
        <MoreHorizIcon />
      </IconButton>
      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleDeleteClick}>
          {customer.information!.archived ? 'Unarchive' : 'Archive'}
        </MenuItem>
        {!customer.information!.archived && (
          <MenuItem onClick={handleMergeClick}>Merge</MenuItem>
        )}
      </Menu>
    </>
  )
}
